import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from "lucide-react";

const Footer = () => {
  return (
    <footer 
      className="bg-[#0A0A0A] border-t border-white/5"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center">
                <span className="text-black font-bold text-xl font-serif">G</span>
              </div>
              <div>
                <span className="text-lg font-semibold tracking-tight">Goldspot</span>
                <span className="text-xs uppercase tracking-widest text-muted-foreground block">Tax Service LLC</span>
              </div>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Professional tax preparation and filing services. Your trusted partner for all individual tax needs.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm uppercase tracking-widest text-[#D4AF37] mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: "Home", path: "/" },
                { name: "Services", path: "/services" },
                { name: "About Us", path: "/about" },
                { name: "Contact", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-muted-foreground hover:text-[#D4AF37] transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm uppercase tracking-widest text-[#D4AF37] mb-6">Services</h4>
            <ul className="space-y-3">
              {[
                "Individual Tax Returns",
                "Tax Preparation",
                "Tax Filing",
                "Tax Consulting",
              ].map((service) => (
                <li key={service}>
                  <span className="text-muted-foreground text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm uppercase tracking-widest text-[#D4AF37] mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground text-sm">
                  738 Longfellow St NW<br />
                  Unit 107<br />
                  Washington, DC
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a 
                  href="tel:+12027042459" 
                  className="text-muted-foreground hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  (202) 704-2459
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a
                  href="https://wa.me/12027042459"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid="footer-whatsapp"
                >
                  WhatsApp: (202) 704-2459
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Send size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a
                  href="https://t.me/+12027042459"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid="footer-telegram"
                >
                  Telegram: (202) 704-2459
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a 
                  href="mailto:tax@goldspottax.com" 
                  className="text-muted-foreground hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid="footer-email"
                >
                  tax@goldspottax.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground text-sm">
                  Open 7 Days a Week<br />
                  9:00 AM - 6:00 PM
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm">
            &copy; {new Date().getFullYear()} Goldspot Tax Service LLC. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link 
              to="/privacy-policy" 
              className="text-muted-foreground hover:text-[#D4AF37] transition-colors text-sm"
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms-of-service" 
              className="text-muted-foreground hover:text-[#D4AF37] transition-colors text-sm"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
